import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarCheck, Phone, ArrowRight, ShieldCheck, Scan, Users, Clock,
  Star, Stethoscope, MapPin, HeartPulse, CheckCircle2, Play, Plus, Activity, Droplet, Target, Eye,
} from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useClinic, waLink } from "../context/ClinicContext";
import { ScrollReveal } from "../components/ScrollReveal";
import { AnimatedCounter } from "../components/AnimatedCounter";
import { SectionHeading } from "../components/SectionHeading";
import { ServiceIcon } from "../components/ServiceIcon";

const LOGO_IMG = "/revive-logo.png";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const item = {
  hidden: { opacity: 0, y: 42, rotateX: -40 },
  show: { opacity: 1, y: 0, rotateX: 0, transition: { type: "spring", stiffness: 90, damping: 14 } },
};

export default function Home() {
  const c = useClinic();
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const { data: doctors = [] } = useQuery({ queryKey: ["doctors"], queryFn: async () => (await api.get("/doctors")).data });
  const { data: testimonials = [] } = useQuery({ queryKey: ["testimonials"], queryFn: async () => (await api.get("/testimonials")).data });
  const { data: gallery = [] } = useQuery({ queryKey: ["gallery"], queryFn: async () => (await api.get("/gallery")).data });

  const stats = c.stats || [];
  const topServices = services.slice(0, 6);

  return (
    <div>
      {/* Floating page toolbar */}
      <div className="relative z-30 flex justify-center px-4 pt-5">
        <nav className="inline-flex items-center gap-0.5 gradient-bg rounded-full p-1.5 shadow-xl shadow-sky-900/25 overflow-x-auto max-w-full" data-testid="home-pill-nav">
          {[["Home", "/"], ["About", "/about"], ["Services", "/services"], ["X-Ray", "/diagnostics"], ["Gallery", "/gallery"], ["Contact", "/contact"]].map(([label, to]) => (
            <Link key={to} to={to} className="px-4 py-2 rounded-full text-sm font-semibold text-white/90 hover:bg-white/20 whitespace-nowrap transition-colors" data-testid={`pill-nav-${label.toLowerCase()}`}>{label}</Link>
          ))}
        </nav>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden gradient-bg-soft grid-pattern">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#2BB673]/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#1A75BC]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={container} initial="hidden" animate="show" style={{ perspective: 900 }}>
            <motion.div variants={item} className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-[#2BB673]/30 rounded-full px-4 py-1.5 text-xs font-semibold text-[#27AE60] mb-6">
              <span className="w-2 h-2 rounded-full bg-[#2BB673] animate-pulse" /> Accepting Walk-ins & Scheduled Bookings
            </motion.div>
            <motion.p variants={item} className="eyebrow mb-3">Revive Family Clinic & X-Ray Diagnostics</motion.p>
            <motion.h1 variants={item} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0F1E36] leading-[1.05]">
              Family Clinic in <span className="gradient-text">New Perungalathur</span>
            </motion.h1>
            <motion.p variants={item} className="mt-6 text-base sm:text-lg leading-relaxed text-[#334E68] max-w-xl">
              Consult a General Physician and Family Doctor with MBBS, MD and a Diploma in Diabetology. Digital X-Ray services are coming soon.
            </motion.p>
            <motion.div variants={item} className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-book-btn" className="gradient-bg btn-glow text-white font-bold px-7 py-3.5 rounded-full flex items-center gap-2">
                <CalendarCheck size={19} /> Book an Appointment
              </Link>
              <Link to="/services" data-testid="hero-services-btn" className="bg-white border-2 border-[#1A75BC]/20 text-[#1A75BC] font-bold px-7 py-3.5 rounded-full flex items-center gap-2 hover:border-[#1A75BC] transition-colors">
                Explore Our Services <ArrowRight size={18} />
              </Link>
              <a href={`tel:${c.phone}`} data-testid="hero-call-btn" className="text-[#0F1E36] font-bold px-5 py-3.5 rounded-full flex items-center gap-2 hover:text-[#1A75BC] transition-colors">
                <Phone size={18} /> Call Now
              </a>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 50, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="relative">
            <div className="relative rounded-[2.5rem] overflow-hidden border border-white/40 h-[380px] sm:h-[460px] bg-[#08152B] shadow-2xl shadow-sky-900/30">
              <div className="absolute inset-0 grid-pattern opacity-20" />
              <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[#1A75BC]/40 blur-3xl" />
              <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#2BB673]/40 blur-3xl" />

              <div className="absolute top-5 left-6 flex items-center gap-2 text-white/60 font-mono text-[11px] tracking-[0.2em]">
                <span className="w-2 h-2 rounded-full bg-[#2BB673] animate-pulse" /> REVIVE · LIVE CARE
              </div>

              {/* Orbit system */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-64 h-64 rounded-full border border-white/10" />
                <div className="absolute w-44 h-44 rounded-full border border-white/10" />

                {/* Outer orbit */}
                <div className="absolute w-64 h-64 animate-spin-slow">
                  {[["top", Stethoscope], ["right", Scan], ["bottom", HeartPulse], ["left", Droplet]].map(([pos, Icon], i) => (
                    <div key={i} className={"absolute " + {
                      top: "left-1/2 -translate-x-1/2 -top-6",
                      right: "top-1/2 -translate-y-1/2 -right-6",
                      bottom: "left-1/2 -translate-x-1/2 -bottom-6",
                      left: "top-1/2 -translate-y-1/2 -left-6",
                    }[pos]}>
                      <div className="animate-spin-slow-rev w-12 h-12 rounded-full glass flex items-center justify-center text-[#0F1E36] shadow-lg">
                        <Icon size={20} className="text-[#1A75BC]" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Inner orbit */}
                <div className="absolute w-44 h-44 animate-spin-slow-rev">
                  {[["top", Activity], ["bottom", Plus]].map(([pos, Icon], i) => (
                    <div key={i} className={"absolute " + (pos === "top" ? "left-1/2 -translate-x-1/2 -top-5" : "left-1/2 -translate-x-1/2 -bottom-5")}>
                      <div className="animate-spin-slow w-10 h-10 rounded-full bg-[#2BB673]/90 flex items-center justify-center text-white shadow-lg">
                        <Icon size={18} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Core */}
                <div className="relative w-28 h-28 rounded-[1.75rem] gradient-bg flex items-center justify-center text-[#2BB673] shadow-[0_0_45px_rgba(43,182,115,0.55)]">
                  <span className="pulse-ring absolute inset-0 rounded-[1.75rem]" />
                  <Plus size={52} strokeWidth={2.5} className="text-white relative z-10" />
                </div>
              </div>

              {/* ECG line */}
              <svg className="absolute bottom-9 left-0 w-full h-14 text-[#00D2FF]" viewBox="0 0 400 60" preserveAspectRatio="none" fill="none">
                <motion.path d="M0 34 H110 l9 -22 l11 42 l9 -30 l13 30 H400" stroke="currentColor" strokeWidth="2.2"
                  initial={{ pathLength: 0, opacity: 0.3 }} animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 2.4, repeat: Infinity, repeatType: "loop", ease: "easeInOut" }} />
              </svg>
            </div>

            <div className="absolute -left-4 sm:-left-8 top-10 glass rounded-2xl shadow-xl p-4 flex items-center gap-3 animate-float">
              <div className="w-11 h-11 rounded-xl gradient-bg flex items-center justify-center text-white"><Scan size={22} /></div>
              <div><div className="font-bold text-[#0F1E36] text-sm">Digital HD X-Ray</div><div className="text-xs text-[#334E68]">Coming soon</div></div>
            </div>
            <div className="absolute -right-2 sm:-right-6 bottom-16 glass rounded-2xl shadow-xl p-4 flex items-center gap-3 animate-float-slow">
              <div className="w-11 h-11 rounded-xl bg-[#EAFBF1] flex items-center justify-center text-[#27AE60]"><HeartPulse size={22} /></div>
              <div><div className="font-bold text-[#0F1E36] text-sm">Family Care</div><div className="text-xs text-[#334E68]">All ages welcome</div></div>
            </div>
            <div className="absolute right-6 top-4 glass rounded-full shadow-lg px-3 py-1.5 flex items-center gap-1 text-sm font-bold text-[#0F1E36] animate-float">
              <Star size={15} className="text-amber-400 fill-amber-400" /> 4.9
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-10 relative z-10">
          <div className="bg-white rounded-3xl shadow-xl shadow-sky-900/5 border border-slate-100 grid grid-cols-3 divide-x divide-slate-100">
            {stats.map((s, i) => (
              <ScrollReveal key={i} delay={i * 0.08} className="p-6 sm:p-8 text-center">
                <div className="text-3xl sm:text-4xl font-extrabold gradient-text font-mono">
                  <AnimatedCounter value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-2 text-xs sm:text-sm font-semibold text-[#627D98]">{s.label}</div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-6">
          <ScrollReveal>
            <div className="h-full bg-white rounded-3xl border border-slate-100 shadow-sm p-8 card-lift">
              <div className="w-14 h-14 rounded-2xl gradient-bg flex items-center justify-center text-white mb-5"><Target size={26} /></div>
              <h3 className="text-xl font-extrabold text-[#0F1E36] mb-3">Our Mission</h3>
              <p className="text-[#627D98] leading-relaxed">To make trusted, compassionate healthcare accessible to every family in our neighbourhood. We provide attentive consultations, preventive care, diabetes management, bone and joint care and timely diagnostics, helping patients make informed decisions about their health.</p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.12}>
            <div className="h-full bg-[#0F1E36] rounded-3xl shadow-sm p-8 card-lift text-white">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center text-[#2BB673] mb-5"><Eye size={26} /></div>
              <h3 className="text-xl font-extrabold mb-3">Our Vision</h3>
              <p className="text-white/80 leading-relaxed">To be the neighbourhood clinic families turn to at every stage of life — known for personal care, clinical integrity and a lasting commitment to healthier lives.</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading eyebrow="What We Offer" title="Complete Care Under One Roof" subtitle="From everyday family medicine to advanced diagnostics, our services are built around your family's wellbeing." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topServices.map((s, i) => (
              <ScrollReveal key={s.id} delay={i * 0.06}>
                <Link to="/services" className="card-lift block h-full bg-white rounded-2xl border border-slate-100 p-7 group" data-testid={`home-service-${i}`}>
                  <div className="w-14 h-14 rounded-2xl gradient-bg-soft flex items-center justify-center text-[#1A75BC] mb-5 group-hover:scale-110 transition-transform">
                    <ServiceIcon name={s.icon} size={26} />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F1E36] mb-2">{s.title}</h3>
                  <p className="text-sm text-[#627D98] leading-relaxed">{s.short_description}</p>
                </Link>
              </ScrollReveal>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="inline-flex items-center gap-2 font-bold text-[#1A75BC]">View all services <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      {/* X-RAY CTA STRIP */}
      <section className="relative overflow-hidden gradient-bg py-16 lg:py-20">
        <div className="absolute inset-0 opacity-20 grid-pattern" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center text-white">
          <ScrollReveal>
            <p className="text-white/70 eyebrow !text-white/70 mb-3">Service Update</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight">Digital X-Ray Services — Coming Soon</h2>
            <p className="mt-4 text-white/85 leading-relaxed max-w-lg">Our X-Ray service is not yet operational. Please contact the clinic for launch updates and current consultation availability.</p>
            <Link to="/diagnostics" className="inline-flex items-center gap-2 mt-7 bg-white text-[#1A75BC] font-bold px-6 py-3 rounded-full btn-glow" data-testid="home-diagnostics-cta">
              <Scan size={18} /> View X-Ray Updates
            </Link>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              {[["X-Ray Service Coming Soon", Scan], ["General Physician Consultation", Stethoscope], ["Monday–Saturday, 6–9 PM", Clock], ["Appointment: 8608323094", Phone]].map(([t, Icon], i) => (
                <div key={i} className="bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/15">
                  <Icon size={24} className="mb-3" />
                  <div className="font-semibold text-sm">{t}</div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* DOCTOR SPOTLIGHT */}
      {doctors[0] && (
        <section className="py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div className="relative">
                <div className="absolute -inset-3 gradient-bg-soft rounded-[2rem] -rotate-2" />
                <img src={fileUrl(doctors[0].image)} alt={doctors[0].name} className="relative rounded-[2rem] w-full max-w-md mx-auto object-cover shadow-xl" />
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.12}>
              <p className="eyebrow mb-3">Meet Our Doctor</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F1E36]">{doctors[0].name}</h2>
              <p className="mt-2 text-[#1A75BC] font-semibold">{doctors[0].qualification}</p>
              <p className="mt-5 text-[#627D98] leading-relaxed line-clamp-5">{doctors[0].description}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {(doctors[0].expertise || []).slice(0, 4).map((e) => (
                  <span key={e} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#EAFBF1] text-[#27AE60] px-3 py-1.5 rounded-full"><CheckCircle2 size={13} /> {e}</span>
                ))}
              </div>
              <Link to="/about" className="inline-flex items-center gap-2 mt-7 font-bold text-[#1A75BC]" data-testid="home-doctor-profile">View full profile <ArrowRight size={18} /></Link>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* NOTE FROM PROPRIETOR */}
      <section className="py-20 lg:py-28 bg-[#F0F7FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-5 gap-12 items-center">
          <ScrollReveal className="lg:col-span-2">
            <div className="relative">
              <div className="absolute -inset-3 gradient-bg-soft rounded-[2rem] rotate-2" />
              <img src="/proprietor.jpg" alt="Dr. Gopinath Duraiswamy, Proprietor" className="relative rounded-[2rem] w-full max-w-sm mx-auto object-cover shadow-xl" data-testid="proprietor-photo" />
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.12} className="lg:col-span-3">
            <p className="eyebrow mb-3">A Note from the Proprietor</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F1E36]">Dr. Gopinath Duraiswamy</h2>
            <p className="mt-2 text-[#1A75BC] font-semibold">Senior Consultant Orthopaedic Surgeon</p>
            <div className="mt-5 space-y-4 text-[#627D98] leading-relaxed">
              <p>Revive Family Clinic believes every family deserves accessible, attentive healthcare built on trust. We aim to care for the whole family, with a focus on general health, preventive care and diabetes management.</p>
              <p>As an orthopaedic surgeon specialising in advanced joint preservation, robotic joint replacement, complex trauma, arthroscopy, sports medicine and regenerative therapies, I have seen the value of early advice and care tailored to each patient. I also serve as a national faculty member in sports medicine.</p>
              <p>At Revive, our team listens carefully, explains treatment options clearly and supports patients through both everyday health concerns and longer-term conditions. We hope to be a dependable neighbourhood clinic where you and your family always feel comfortable seeking care.</p>
            </div>
            <div className="mt-6 border-l-4 border-[#2BB673] pl-4">
              <div className="font-bold text-[#0F1E36]">Dr. Gopinath Duraiswamy</div>
              <div className="text-sm text-[#627D98]">Senior Consultant Orthopaedic Surgeon</div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="py-20 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <SectionHeading eyebrow="Gallery" title="Inside Revive" subtitle="A glimpse of our clinic, facilities, team and community health initiatives." />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.slice(0, 8).map((it, i) => (
                <ScrollReveal key={it.id} delay={(i % 4) * 0.05}>
                  <Link to="/gallery" className="group relative block rounded-2xl overflow-hidden card-lift aspect-square" data-testid={`home-gallery-${i}`}>
                    {it.media_type === "video" ? (
                      <div className="relative w-full h-full bg-black">
                        <video src={fileUrl(it.url)} className="w-full h-full object-cover" muted />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30"><div className="w-11 h-11 rounded-full bg-white/90 flex items-center justify-center"><Play size={20} className="text-[#1A75BC] ml-0.5" /></div></div>
                      </div>
                    ) : (
                      <img src={fileUrl(it.url)} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    )}
                  </Link>
                </ScrollReveal>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/gallery" className="inline-flex items-center gap-2 font-bold text-[#1A75BC]">View full gallery <ArrowRight size={18} /></Link>
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <ScrollReveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-[#0F1E36] p-10 lg:p-16 text-center text-white">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#2BB673]/20 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#1A75BC]/30 blur-3xl" />
              <div className="relative">
                <Stethoscope size={40} className="mx-auto mb-5 text-[#2BB673]" />
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold">Your family's health, our priority</h2>
                <p className="mt-4 text-slate-300 max-w-xl mx-auto">Book an appointment today and experience healthcare that treats you like family.</p>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                  <Link to="/contact" className="gradient-bg btn-glow text-white font-bold px-7 py-3.5 rounded-full flex items-center gap-2"><CalendarCheck size={18} /> Book Appointment</Link>
                  <a href={waLink(c.whatsapp)} target="_blank" rel="noreferrer" className="bg-white/10 border border-white/20 text-white font-bold px-7 py-3.5 rounded-full flex items-center gap-2"><MapPin size={18} /> Find Us</a>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
